
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { SecureInput } from "@/components/security/SecureInput";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";
import { emailSchema } from "@/lib/security/input-validation";
import { useGoogleOAuth } from "@/hooks/use-google-oauth";
import { safeMixpanelTrack } from "@/lib/mixpanel-utils";

interface LoginFormData {
  email: string;
  password: string;
}

interface LoginFormProps {
  redirectPath?: string;
}

export function LoginForm({ redirectPath = '/meeting' }: LoginFormProps) {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { signInWithGoogle, loading: googleLoading } = useGoogleOAuth();
  const form = useForm<LoginFormData>({
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onChange'
  });
  
  // Removed excessive logging that was causing console spam
  
  const onSubmit = async (data: LoginFormData) => {
    try {
      setLoading(true);
      
      // Validate email format
      const emailValidation = emailSchema.safeParse(data.email);
      if (!emailValidation.success) {
        throw new Error("Invalid email format");
      }
      
      console.log("[LoginForm] Login attempt started:", {
        email: emailValidation.data,
        redirectPath,
        timestamp: new Date().toISOString()
      });
      
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: emailValidation.data,
        password: data.password,
      });

      if (error) throw error;

      console.log("[LoginForm] Login successful:", {
        timestamp: new Date().toISOString(),
        redirectPath,
        userId: authData.user?.id,
        hasSession: !!authData.session
      });

      // Track Sign In event in Mixpanel
      if (authData.user) {
        safeMixpanelTrack('Sign In', {
          user_id: authData.user.id,
          login_method: 'email',
          success: true,
        });
      }

      toast({
        title: "Welcome back!",
        description: "You have successfully logged in.",
      });
      
      // The redirect will be handled by LoginPage component using the auth state change
    } catch (error: any) {
      console.error("[LoginForm] Login error:", error.message, {
        timestamp: new Date().toISOString()
      });

      // Track failed Sign In event in Mixpanel
      safeMixpanelTrack('Sign In', {
        login_method: 'email',
        success: false,
      });
      
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    // Pass the redirectPath to ensure login flows go to the correct destination
    await signInWithGoogle(false, { redirectTo: `${window.location.origin}${redirectPath}` });
  };

  const handleResetPassword = () => {
    // Handle reset password route
    window.location.href = '/reset-password';
  };

  return (
    <div className="space-y-4">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            rules={{
              required: "Email is required",
              validate: (value) => {
                const result = emailSchema.safeParse(value);
                return result.success || "Invalid email format";
              }
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <SecureInput type="email" placeholder="Enter your email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            rules={{
              required: "Password is required",
              minLength: {
                value: 6,
                message: "Password must be at least 6 characters"
              }
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <SecureInput type="password" placeholder="Enter your password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-purple-600 to-orange-600 hover:to-orange-500 hover:from-purple-500  text-white transition-all duration-0"
            disabled={loading}
          >
            {loading ? "Logging in..." : "Sign In"}
          </Button>
        </form>
      </Form>
      
      <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
        <span className="relative z-10 bg-background px-2 text-muted-foreground">
          Or continue with
        </span>
      </div>
      
      <Button
        variant="outline"
        type="button"
        className="w-full"
        onClick={handleGoogleLogin}
        disabled={loading || googleLoading}
      >
        <img 
          src="/assets/images/ui/google-g-logo.svg" 
          alt="Google" 
          className="w-6 h-6 mr-3"
        />
        {googleLoading ? "Connecting..." : "Sign in with Google"}
      </Button>
      
      <div className="flex flex-col gap-2 text-center text-sm">
        <Button
          variant="link"
          type="button"
          className="h-auto p-0"
          onClick={handleResetPassword}
        >
          Forgot password?
        </Button>
        
        <div>
          Don't have an account?{" "}
          <Link to="/signup" className="text-primary underline underline-offset-4 hover:text-primary/80">
            Sign up
          </Link>
        </div>
      </div>
    </div>
  );
}
